import React from 'react';
import { FaGithub, FaPlus, FaMinus, FaCheck, FaTimes, FaBook } from 'react-icons/fa';
import NewTooltip from '../../atoms/NewTooltip';

interface Repository {
    name: string;
    repository: string | null | undefined;
    description: string | null | undefined;
    language: string | null | undefined;
    version: string | null | undefined;
    project_version: string | null | undefined;
    frameworks: Record<string, string>;
    package_manager: string | null | undefined;
    subprojects: any[];
    test_coverage: any;
    dependencies: any[];
    infrastructure: any;
    active: boolean;
    readme: boolean;
    ignored_dirs: string[];
}

interface SoftwarePortfolioTableRowsProps {
    rows: Repository[];
    expandedProjects: Set<string>;
    getCoveragePercentage: (testCoverage: any) => number;
    getGitHubUrl: (repository: string | null | undefined) => string | null;
    toggleProject: (projectName: string) => void;
    openModal: (title: string, data: any, type: 'frameworks' | 'dependencies' | 'ignored_dirs') => void;
}

const SoftwarePortfolioTableRows: React.FC<SoftwarePortfolioTableRowsProps> = ({
    rows,
    expandedProjects,
    getCoveragePercentage,
    getGitHubUrl,
    toggleProject,
    openModal
}) => {
    if (!rows || rows.length === 0) {
        return (
            <tr>
                <td colSpan={11} className="py-8 px-2 text-center">
                    <div className="text-2xl font-bold text-gray-500">Nenhum repositório encontrado...</div>
                </td>
            </tr>
        );
    }

    const allRows: JSX.Element[] = [];

    rows.forEach((repo: Repository, index: number) => {
        const isExpanded = expandedProjects.has(repo.name);
        const hasSubprojects = repo.subprojects && repo.subprojects.length > 0;

        // Linha principal do projeto
        allRows.push(
            <tr
                key={repo.name}
                className={`${index % 2 === 0 ? 'bg-slate-100 dark:bg-slate-800' : 'dark:bg-slate-900'} hover:bg-slate-300 hover:shadow-md border-b border-gray-300`}
            >
                <td className="py-2 px-2 font-medium">
                    <div className="flex items-center gap-2">
                        {hasSubprojects && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleProject(repo.name);
                                }}
                                className="text-gray-600 hover:text-gray-800 transition-colors"
                            >
                                {isExpanded ? <FaMinus size={12} /> : <FaPlus size={12} />}
                            </button>
                        )}
                        {repo.description ? (
                            <NewTooltip content={repo.description} alignment="top">
                                <span className="cursor-help">{repo.name}</span>
                            </NewTooltip>
                        ) : (
                            <span>{repo.name}</span>
                        )}
                    </div>
                </td>
                <td className="py-2 px-2">
                    <span className="text-sm font-medium text-gray-700">
                        {repo.project_version || '-'}
                    </span>
                </td>
                <td className="py-2 px-2 text-gray-500">-</td>
                <td className="py-2 px-2">
                    <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${repo.language === 'java' ? 'bg-orange-100 text-orange-800' :
                                repo.language === 'python' ? 'bg-blue-100 text-blue-800' :
                                    repo.language === 'javascript' ? 'bg-yellow-100 text-yellow-800' :
                                        'bg-gray-100 text-gray-800'
                            }`}>
                            {repo.language || 'N/A'}
                        </span>
                        {repo.version && (
                            <span className="text-xs text-gray-600">{repo.version}</span>
                        )}
                    </div>
                </td>
                <td className="py-2 px-2">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${repo.package_manager === 'maven' ? 'bg-red-100 text-red-800' :
                            repo.package_manager === 'npm' ? 'bg-green-100 text-green-800' :
                                repo.package_manager === 'pip' ? 'bg-blue-100 text-blue-800' :
                                    repo.package_manager === 'poetry' ? 'bg-purple-100 text-purple-800' :
                                        'bg-gray-100 text-gray-800'
                        }`}>
                        {repo.package_manager || 'N/A'}
                    </span>
                </td>
                <td className="py-2 px-2 text-center">
                    {Object.keys(repo.frameworks || {}).length > 0 ? (
                        <button
                            className="text-blue-600 underline hover:text-blue-800"
                            onClick={(e) => {
                                e.stopPropagation();
                                openModal(`Frameworks - ${repo.name}`, repo.frameworks, 'frameworks');
                            }}
                        >
                            {Object.keys(repo.frameworks || {}).length}
                        </button>
                    ) : (
                        <span>0</span>
                    )}
                </td>
                <td className="py-2 px-2 text-center">
                    {repo.dependencies?.length > 0 ? (
                        <button
                            className="text-blue-600 underline hover:text-blue-800"
                            onClick={(e) => {
                                e.stopPropagation();
                                openModal(`Dependências - ${repo.name}`, repo.dependencies, 'dependencies');
                            }}
                        >
                            {repo.dependencies.length}
                        </button>
                    ) : (
                        <span>0</span>
                    )}
                </td>
                <td className="py-2 px-2 text-center">
                    {repo.ignored_dirs?.length > 0 ? (
                        <button
                            className="text-orange-600 underline hover:text-orange-800"
                            onClick={(e) => {
                                e.stopPropagation();
                                openModal(`Diretórios Ignorados - ${repo.name}`, repo.ignored_dirs, 'ignored_dirs');
                            }}
                        >
                            {repo.ignored_dirs.length}
                        </button>
                    ) : (
                        <span>0</span>
                    )}
                </td>
                <td className="py-2 px-2">
                    {repo.test_coverage ? (
                        <div className="flex items-center gap-2">
                            <div className="w-16 bg-gray-200 rounded-full h-2 dark:bg-gray-700">
                                <div
                                    className="bg-blue-600 h-2 rounded-full"
                                    style={{ width: `${getCoveragePercentage(repo.test_coverage)}%` }}
                                ></div>
                            </div>
                            <span className="text-xs text-gray-600 min-w-[30px]">
                                {getCoveragePercentage(repo.test_coverage)}%
                            </span>
                        </div>
                    ) : (
                        <span className="text-gray-400 text-xs">N/A</span>
                    )}
                </td>
                <td className="py-2 px-2 text-center">
                    {repo.active ? (
                        <FaCheck className="text-green-600 mx-auto" size={16} />
                    ) : (
                        <FaTimes className="text-red-600 mx-auto" size={16} />
                    )}
                </td>
                <td className="py-2 px-2 text-center">
                    <div className="flex items-center justify-center gap-2">
                        {repo.readme && repo.repository && (
                            <a
                                href={`${getGitHubUrl(repo.repository)}/blob/main/README.md`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 transition-colors"
                                onClick={(e) => e.stopPropagation()}
                                title="Ver README"
                            >
                                <FaBook size={16} />
                            </a>
                        )}
                        {repo.repository ? (
                            <a
                                href={getGitHubUrl(repo.repository) || '#'}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-gray-600 hover:text-gray-800 transition-colors"
                                onClick={(e) => e.stopPropagation()}
                                title="Ver repositório"
                            >
                                <FaGithub size={18} />
                            </a>
                        ) : (
                            <span className="text-gray-300">-</span>
                        )}
                    </div>
                </td>
            </tr>
        );

        // Linhas dos subprojetos (se expandido)
        if (isExpanded && hasSubprojects) {
            repo.subprojects.forEach((sub, subIndex) => {
                allRows.push(
                    <tr
                        key={`${repo.name}-sub-${subIndex}`}
                        className="bg-blue-50 dark:bg-blue-900 border-b border-gray-200"
                    >
                        <td className="py-2 px-2 pl-8 text-sm text-gray-700">
                            <span className="text-blue-600">└─ {sub.name}</span>
                        </td>
                        <td className="py-2 px-2">
                            <span className="text-sm text-gray-600">
                                {sub.project_version || '-'}
                            </span>
                        </td>
                        <td className="py-2 px-2 text-gray-500">-</td>
                        <td className="py-2 px-2">
                            <div className="flex items-center gap-2">
                                <span className={`px-2 py-1 rounded text-xs font-medium ${sub.language === 'java' ? 'bg-orange-100 text-orange-800' :
                                        sub.language === 'python' ? 'bg-blue-100 text-blue-800' :
                                            sub.language === 'javascript' ? 'bg-yellow-100 text-yellow-800' :
                                                'bg-gray-100 text-gray-800'
                                    }`}>
                                    {sub.language || 'N/A'}
                                </span>
                                {sub.version && (
                                    <span className="text-xs text-gray-600">{sub.version}</span>
                                )}
                            </div>
                        </td>
                        <td className="py-2 px-2">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${sub.package_manager === 'maven' ? 'bg-red-100 text-red-800' :
                                    sub.package_manager === 'npm' ? 'bg-green-100 text-green-800' :
                                        sub.package_manager === 'pip' ? 'bg-blue-100 text-blue-800' :
                                            sub.package_manager === 'poetry' ? 'bg-purple-100 text-purple-800' :
                                                'bg-gray-100 text-gray-800'
                                }`}>
                                {sub.package_manager || 'N/A'}
                            </span>
                        </td>
                        <td className="py-2 px-2 text-center">
                            {Object.keys(sub.frameworks || {}).length > 0 ? (
                                <button
                                    className="text-blue-600 underline hover:text-blue-800"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        openModal(`Frameworks - ${sub.name}`, sub.frameworks, 'frameworks');
                                    }}
                                >
                                    {Object.keys(sub.frameworks || {}).length}
                                </button>
                            ) : (
                                <span>0</span>
                            )}
                        </td>
                        <td className="py-2 px-2 text-center">
                            {sub.dependencies?.length > 0 ? (
                                <button
                                    className="text-blue-600 underline hover:text-blue-800"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        openModal(`Dependências - ${sub.name}`, sub.dependencies, 'dependencies');
                                    }}
                                >
                                    {sub.dependencies.length}
                                </button>
                            ) : (
                                <span>0</span>
                            )}
                        </td>
                        <td className="py-2 px-2 text-center">
                            <span>0</span>
                        </td>
                        <td className="py-2 px-2">
                            {sub.test_coverage ? (
                                <div className="flex items-center gap-2">
                                    <div className="w-16 bg-gray-200 rounded-full h-2 dark:bg-gray-700">
                                        <div
                                            className="bg-blue-600 h-2 rounded-full"
                                            style={{ width: `${getCoveragePercentage(sub.test_coverage)}%` }}
                                        ></div>
                                    </div>
                                    <span className="text-xs text-gray-600 min-w-[30px]">
                                        {getCoveragePercentage(sub.test_coverage)}%
                                    </span>
                                </div>
                            ) : (
                                <span className="text-gray-400 text-xs">N/A</span>
                            )}
                        </td>
                        <td className="py-2 px-2 text-center">
                            {sub.active ? (
                                <FaCheck className="text-green-600 mx-auto" size={16} />
                            ) : (
                                <FaTimes className="text-red-600 mx-auto" size={16} />
                            )}
                        </td>
                        <td className="py-2 px-2 text-center">
                            <div className="flex items-center justify-center gap-2">
                                {sub.readme && repo.repository && (
                                    <a
                                        href={`${getGitHubUrl(repo.repository)}/blob/main/${sub.name}/README.md`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-600 hover:text-blue-800 transition-colors"
                                        onClick={(e) => e.stopPropagation()}
                                        title="Ver README"
                                    >
                                        <FaBook size={16} />
                                    </a>
                                )}
                                <span className="text-gray-300">-</span>
                            </div>
                        </td>
                    </tr>
                );
            });
        }
    });

    return <>{allRows}</>;
};

export default SoftwarePortfolioTableRows;
